import { CartProductSchema, OperationType, ProductStatus } from '../../types';
import { State } from '../types';
import { PARTNER_DISCOUNT_TYPE } from '@css-xp/ppc-constants';
import { getBillEstimateV2 } from '@atlassian/cssxp-proxies';
import { v4 as generateUuid } from 'uuid';
import { OmsCapability, getSkipTrialPayload, CustomOrderEntitlement } from './utils';
import omit from 'lodash/omit';
import isEmpty from 'lodash/isEmpty';
import keyBy from 'lodash/keyBy';
import cloneDeep from 'lodash/cloneDeep';
import { Currency, PatchOperation, PricingPlanType } from '@atlassian/cssxp-typescommon';
import { OrderItemType } from '@atlassian/cssxp-types-entitlement';
import { isFreeOffering } from './is-free-offering';
import { getPricingPlanId } from './get-pricing-plan-id';
import { isCollectionProductEntitlement } from '@css-xp/ppc-utils';

type OmsPayload = Parameters<typeof getBillEstimateV2>[0];
/**
 * This method returns the Amendment and Patch order types for the Transfer Use-cases
 * @param entitlementsForOMS
 * @param initialStoreProducts
 * @param cartProducts
 * @param selectedProducts
 * @param omsCapability
 * @param offeringsAndpricingPlans
 * @param partnerPromotions
 * @param isNonCommercialPricingEnabled
 * @param currentPricingEligibility
 * @param currentCurrency
 */
export const getProductsAmendmentAndPatchPayloadFromTransfer = ({
  initialStoreProducts,
  entitlementsForOMS,
  cartProducts,
  selectedProducts,
  omsCapability,
  offeringsAndpricingPlans,
  partnerPromotions,
  isNonCommercialPricingEnabled,
  currentPricingEligibility,
  currentCurrency,
}: {
  initialStoreProducts?: State['products'];
  entitlementsForOMS: OmsPayload['entitlements'];
  cartProducts: CartProductSchema[];
  selectedProducts: State['selectedProducts'];
  omsCapability: OmsCapability;
  offeringsAndpricingPlans: State['offeringsAndpricingPlans'];
  partnerPromotions: State['partnerPromotions'];
  isNonCommercialPricingEnabled: boolean;
  currentPricingEligibility?: `${PricingPlanType}`;
  currentCurrency?: `${Currency}`;
}): OmsPayload['entitlements'] => {
  // since we are sending Creation order type for bill estimation,
  // we don't need to send the same product as amendment or patch
  if (omsCapability === OmsCapability.BILL_ESTIMATE) {
    return [];
  }

  const payloadLineItems: OmsPayload['entitlements'] = [];

  const transferOnlyProducts = cartProducts.filter(
    ({ operationType, productStatuses }) =>
      operationType === OperationType.TRANSFER && !productStatuses?.includes(ProductStatus.EDITED)
  );

  const transferWithAmendmentProducts = cartProducts.filter(
    ({ operationType, productStatuses }) =>
      operationType === OperationType.TRANSFER && productStatuses?.includes(ProductStatus.EDITED)
  );

  const entitlementIdToOMSDetailMap = keyBy(entitlementsForOMS, 'entitlementId');

  // for transfer only products we need to handle the partner promotions
  if (transferOnlyProducts && transferOnlyProducts.length) {
    const promotionPayload = transferOnlyProducts
      .map((product: CartProductSchema) => {
        const entitlementAsAccountModificationPayload = cloneDeep(entitlementIdToOMSDetailMap[product.id]);
        const partnerPromotionId = partnerPromotions[product.productId];
        // removing promotions part for amendment order
        delete entitlementAsAccountModificationPayload['addPromotions'];

        const existingPartnerPromotionIndex =
          product?.promotions?.findIndex(({ type }) => type === PARTNER_DISCOUNT_TYPE) ?? -1;

        const isProductRelatedToDirectCustomer = !product.isPartnerEntitlement;

        const cloudSiteId = product.cloudSiteId;

        const offerings = offeringsAndpricingPlans?.[product.productId] || [];
        const selectedOfferingDetails = offerings.find(({ id: offeringId }) => offeringId === product.offeringId);
        const isSelectedOfferingFree = isFreeOffering(selectedOfferingDetails?.pricingType ?? '');
        if (isSelectedOfferingFree || !partnerPromotionId) {
          /**
           * For transfer only case for free entitlements, no need of any amendment line to add promotion.
           * This is valid for both direct and partner entitlements.
           */
          return {} as CustomOrderEntitlement & { entitlementId: string; orderType: OrderItemType; productId: string };
        }
        const POELineItemArg: Record<string, string> = !isProductRelatedToDirectCustomer ? { POELineItem: 'true' } : {};
        // for direct customer entitlement add promotion
        // for partner entitlement having no existing partner promotion, add a promotion
        if (isProductRelatedToDirectCustomer || existingPartnerPromotionIndex < 0) {
          const metadataArg = { metadata: { siteId: cloudSiteId, ...POELineItemArg } };
          const addPromotions = [
            {
              promotionInstanceId: null,
              promotionDefinition: {
                promotionId: partnerPromotionId,
                promotionCode: null,
                customisedValues: null,
              },
            },
          ];
          const addPromotionsArg = !isSelectedOfferingFree ? { addPromotions } : {};

          return {
            ...entitlementAsAccountModificationPayload,
            itemId: generateUuid(),
            isImmediate: true,
            orderType: OrderItemType.AMENDMENT_ORDER,
            ...addPromotionsArg,
            ...metadataArg,
          };
        } else {
          const metadataArg = { metadata: { siteId: cloudSiteId, ...POELineItemArg } };

          return {
            entitlementId: product.id,
            orderType: OrderItemType.PATCH_ENTITLEMENT_DETAILS_ORDER,
            productId: product.productId,
            ...metadataArg,
            operations: [
              {
                op: 'replace' as PatchOperation,
                path: '/subscription/promotionInstances/' + existingPartnerPromotionIndex,
                value: {
                  promotionId: partnerPromotionId,
                },
              },
            ],
          };
        }
      })
      .filter((payload) => !isEmpty(payload));
    /**
     * Handling the case for partner to partner transfer.
     * For partner to partner transfer, we need to send the pricing plan change amendment order.
     * This is only when the price eligibility changes for the product.
     */
    const pricingPlanPayload = transferOnlyProducts
      .map((product: CartProductSchema) => {
        const entitlementAsAccountModificationPayload = cloneDeep(entitlementIdToOMSDetailMap[product.id]);
        // removing promotions and nextBillDate part for amendment order as this is just for pricingPlan change
        delete entitlementAsAccountModificationPayload['addPromotions'];
        delete entitlementAsAccountModificationPayload['nextBillingAnchorTimestamp'];
        const offerings = offeringsAndpricingPlans?.[product.productId] || [];
        const pricingPlanIdBasedOnPricingEligibilityAndOffering = (() => {
          if (currentPricingEligibility && currentCurrency) {
            const pricingPlanId = getPricingPlanId({
              pricingEligibility: currentPricingEligibility,
              currency: currentCurrency,
              offerings,
              offeringId: product.offeringId,
              defaultPricingEligibilityToCommercial: isNonCommercialPricingEnabled,
              enableRetainPricingPlan: isNonCommercialPricingEnabled,
              initialPricingPlanId: product.pricingPlanId,
              initialOfferingId: initialStoreProducts?.[product.id]?.offeringId ?? '',
              pricingPlanType: product.pricingPlanType,
              entitlementPriceEligibility: product.priceEligibility,
            });
            if (pricingPlanId) return pricingPlanId;
          }
          return '';
        })();
        /**
         * Sending amendment order for pricing plan change only when the pricing plan id changes.
         * This is valid for partner entitlements.
         */
        const isPricingPlanIdChangeRequired = isNonCommercialPricingEnabled
          ? pricingPlanIdBasedOnPricingEligibilityAndOffering !== product.pricingPlanId
          : false;
        if (!isPricingPlanIdChangeRequired || !product.isPartnerEntitlement) {
          return {} as CustomOrderEntitlement;
        }
        return {
          ...entitlementAsAccountModificationPayload,
          itemId: generateUuid(),
          isImmediate: true,
          orderType: OrderItemType.AMENDMENT_ORDER,
          productId: product.productId,
          entitlementId: product.id,
        };
      })
      .filter((payload): payload is CustomOrderEntitlement => !isEmpty(payload));
    payloadLineItems.push(...promotionPayload, ...pricingPlanPayload);
  }

  if (!transferWithAmendmentProducts?.length) {
    return payloadLineItems;
  }

  const transferWithAmendmentPayload = transferWithAmendmentProducts.map((product: CartProductSchema) => {
    const entitlementAsAccountModificationPayload = cloneDeep(entitlementIdToOMSDetailMap[product.id]);
    const partnerPromotionId = partnerPromotions[product.productId];
    const isProductRelatedToDirectCustomer = !product.isPartnerEntitlement;

    // removing promotions part for amendment order
    delete entitlementAsAccountModificationPayload['addPromotions'];

    const skipTrialArg = getSkipTrialPayload({
      initialOfferingId: initialStoreProducts?.[product.id].offeringId ?? '',
      updatedOfferingId: entitlementAsAccountModificationPayload.offeringId as string,
      isCurrentlyInTrial: product.inTrial === true,
      productId: entitlementAsAccountModificationPayload.productId,
      offeringsAndpricingPlans,
      omsCapability,
    });

    // For org product instance, we get non empty value for parentEntitlementId
    const isOrgInstance =
      product.parentEntitlementId &&
      isCollectionProductEntitlement(initialStoreProducts?.[product.parentEntitlementId].productId);

    /**
     * For Org instances, skip trial is dependant on the parent product's trial status.
     * Hence, we need to send the skip trial payload as per the parent product.
     */
    let skipTrialArgsForInstance = {};
    // isOrgInstance is already checking for parentEntitlementId,
    // but adding this check to confirm it is not null for places used withing this block
    if (isOrgInstance && product.parentEntitlementId) {
      const parentProduct = cartProducts.find(({ id }) => id === product.parentEntitlementId);
      skipTrialArgsForInstance = getSkipTrialPayload({
        initialOfferingId: initialStoreProducts?.[product.parentEntitlementId]?.offeringId ?? '',
        updatedOfferingId: parentProduct?.offeringId ?? '',
        productId: parentProduct?.productId ?? '',
        isCurrentlyInTrial: parentProduct?.inTrial === true,
        offeringsAndpricingPlans: offeringsAndpricingPlans || {},
        omsCapability: omsCapability,
      });
    }

    const cloudSiteId = product.cloudSiteId;
    const POELineItemArg: Record<string, string> = !isProductRelatedToDirectCustomer ? { POELineItem: 'true' } : {};
    const metadataArg = { metadata: { siteId: cloudSiteId, ...POELineItemArg } };

    const addPromotions = partnerPromotionId
      ? [
          {
            promotionInstanceId: null,
            promotionDefinition: {
              promotionId: partnerPromotionId,
              promotionCode: null,
              customisedValues: null,
            },
          },
        ]
      : [];
    const offerings = offeringsAndpricingPlans?.[product.productId] || [];
    const selectedOfferingDetails = offerings.find(({ id: offeringId }) => offeringId === product.offeringId);
    const isSelectedOfferingFree = isFreeOffering(selectedOfferingDetails?.pricingType ?? '');
    const existingPartnerPromotionIndex =
      product?.promotions?.findIndex(({ type }) => type === PARTNER_DISCOUNT_TYPE) ?? -1;

    // Since POE changes are scheduled promotions for Direct to Partner transfer for paid entitlements only,
    // we are sending amendment order with immediate partner promotion
    let addPromotionsArg;
    if (isProductRelatedToDirectCustomer || existingPartnerPromotionIndex < 0) {
      // Do not send the add promotions when product is moving to free offering
      addPromotionsArg = !isSelectedOfferingFree ? { addPromotions } : {};
    }

    const isBillDateTheOnlyChange = (() => {
      // Now next bill date will be added to changed products hash.
      // We are still checking on the global store level change because it is not handled for all use-cases:
      // product addition after setting cloud-site level bill date.
      const currentProductChanges = selectedProducts[product.id];
      const otherChanges = omit(currentProductChanges, ['productStatuses', 'nextBillDate']);
      const isPricingPlanChange = entitlementAsAccountModificationPayload?.pricingPlanId !== product.pricingPlanId;
      return product.nextBillDate && isEmpty(otherChanges) && !isPricingPlanChange;
    })();

    if (isBillDateTheOnlyChange) {
      return {
        entitlementId: product.id,
        orderType: OrderItemType.AMENDMENT_ORDER,
        isImmediate: true,
        productId: product.productId,
        offeringId: product.offeringId,
        nextBillingAnchorTimestamp: Number(product.nextBillDate),
        ...metadataArg,
        ...addPromotionsArg,
      };
    }

    return {
      ...entitlementAsAccountModificationPayload,
      itemId: generateUuid(),
      isImmediate: true,
      orderType: OrderItemType.AMENDMENT_ORDER,
      ...addPromotionsArg,
      ...metadataArg,
      ...(isOrgInstance ? skipTrialArgsForInstance : skipTrialArg),
    };
  });
  payloadLineItems.push(...transferWithAmendmentPayload);

  const patchEntitlementForPromotionPayload = transferWithAmendmentProducts
    .filter((product) => product?.promotions?.some(({ type }) => type === PARTNER_DISCOUNT_TYPE))
    .map((product: CartProductSchema) => {
      const cloudSiteId = product.cloudSiteId;
      const metadataArg = { metadata: { siteId: cloudSiteId, POELineItem: 'true' } };

      const existingPartnerPromotionIndex = product?.promotions?.findIndex(
        ({ type }) => type === PARTNER_DISCOUNT_TYPE
      );

      const partnerPromotionId = partnerPromotions[product.productId];

      /**
       * Fetching the updated offeringId for the selected transfer product
       * If it is free offering, then remove the promotion from the product
       * Adding a remove patch operation for the promotion instance
       * */
      const offeringIdForSelectedTransferProduct = selectedProducts[product.id]?.offeringId;
      const offerings = offeringsAndpricingPlans?.[product.productId] || [];
      const selectedOfferingDetails = offerings.find(
        ({ id: offeringId }) => offeringId === offeringIdForSelectedTransferProduct
      );
      const isSelectedOfferingFree = isFreeOffering(selectedOfferingDetails?.pricingType ?? '');

      if (isSelectedOfferingFree) {
        return {
          entitlementId: product.id,
          orderType: OrderItemType.PATCH_ENTITLEMENT_DETAILS_ORDER,
          productId: product.productId,
          ...metadataArg,
          operations: [
            {
              op: 'remove' as PatchOperation,
              path: '/subscription/promotionInstances/' + existingPartnerPromotionIndex,
            },
          ],
        };
      }

      if (!partnerPromotionId) {
        return {};
      }

      return {
        entitlementId: product.id,
        orderType: OrderItemType.PATCH_ENTITLEMENT_DETAILS_ORDER,
        productId: product.productId,
        ...metadataArg,
        operations: [
          {
            op: 'replace' as PatchOperation,
            path: '/subscription/promotionInstances/' + existingPartnerPromotionIndex,
            value: {
              promotionId: partnerPromotionId,
            },
          },
        ],
      };
    })
    .filter((payload): payload is CustomOrderEntitlement => !isEmpty(payload));

  payloadLineItems.push(...patchEntitlementForPromotionPayload);

  return payloadLineItems;
};
